'use client';

import { useEffect } from 'react';
import { connectExec } from '@/lib/ws-client';
import { LatencyStrip } from '@/components/latency-strip';
import { useStore } from '@/lib/store';
import { togglePause, useRiskConfig } from '@/lib/risk/limits';
import { cn } from '@/lib/cn';

const RECONNECTING_THRESHOLD_ATTEMPTS = 4;

export default function TradingLayout({ children }: { children: React.ReactNode }) {
  const connected = useStore((s) => s.connected);
  const attempts = useStore((s) => s.reconnectAttempts);
  const risk = useRiskConfig();
  const reconnecting = !connected && attempts > 0 && attempts <= RECONNECTING_THRESHOLD_ATTEMPTS;
  const offline = !connected && attempts > RECONNECTING_THRESHOLD_ATTEMPTS;
  const connTone = connected
    ? 'text-ok'
    : reconnecting
      ? 'text-warn'
      : offline
        ? 'text-err'
        : 'text-muted';
  const connLabel = connected
    ? 'executor online'
    : reconnecting
      ? 'reconnecting…'
      : offline
        ? 'executor offline'
        : 'connecting…';

  useEffect(() => {
    const cleanup = connectExec();
    return cleanup;
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="px-4 py-2 border-b border-border flex items-center gap-4">
        <a href="/games" className="font-bold text-accent-strong hover:opacity-80">LadderBook</a>
        <nav className="flex gap-3 text-muted">
          <a href="/games" className="hover:text-text">sports</a>
          <a href="/positions" className="hover:text-text">positions</a>
          <a href="/profile" className="hover:text-text">profile</a>
        </nav>
        <div className="ml-auto flex items-center gap-3">
          <button
            type="button"
            onClick={() => togglePause()}
            className={cn(
              'rounded border px-2 py-0.5 text-[11px] uppercase tracking-wider transition-colors',
              risk.paused
                ? 'border-err bg-err/15 text-err hover:bg-err/25'
                : 'border-border text-muted hover:text-text',
            )}
            title="Pause/resume all order submission from this browser"
          >
            {risk.paused ? '■ Paused' : '▶ Live'}
          </button>
          <span className={cn('text-xs', connTone)}>{connLabel}</span>
        </div>
      </header>
      {risk.paused ? (
        <div className="px-4 py-1 text-xs border-b border-border bg-err/10 text-err">
          Trading paused — all submits are blocked client-side. Toggle above to resume.
        </div>
      ) : (
        <div className="px-4 py-1 text-xs border-b border-border bg-panel text-muted">
          Power-user view. Trading is vault-gated — paste your Polymarket and (when supported) Kalshi keys in
          {' '}<a href="/profile" className="text-accent hover:underline">/profile</a>{' '}
          to enable trading from the game page. Caps + pause toggle live in the same place.
        </div>
      )}
      <main className="flex-1 overflow-hidden">{children}</main>
      <LatencyStrip />
    </div>
  );
}
